import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Badge,
  Button,
  Card,
  Option,
  Select,
  Spinner,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import {
  ArrowLeft24Regular,
  Send24Regular,
} from '@fluentui/react-icons'
import { api, AdminMessage, AdminTicketDetail as AdminTicketDetailType } from '../lib/api'
import { useConfig } from '../context/ConfigContext'
import { TICKET_STATUS_KEYS, ticketStatusColor, ticketStatusLabel } from '../lib/ticket-status'

const useStyles = makeStyles({
  root: {
    maxWidth: '900px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '700',
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    marginBottom: '6px',
  },
  section: {
    padding: '14px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  messages: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  messageItem: {
    padding: '10px 12px',
    borderRadius: '8px',
    background: tokens.colorNeutralBackground3,
  },
  meta: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    marginBottom: '4px',
  },
  content: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.4',
    whiteSpace: 'pre-wrap',
  },
  composer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
})

function formatDate(dateString: string) {
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function AdminTicketDetail() {
  const styles = useStyles()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { config } = useConfig()

  const [ticket, setTicket] = useState<AdminTicketDetailType | null>(null)
  const [messages, setMessages] = useState<AdminMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [savingStatus, setSavingStatus] = useState(false)
  const [sending, setSending] = useState(false)
  const [reply, setReply] = useState('')

  const [statusDraft, setStatusDraft] = useState('')
  const [priorityDraft, setPriorityDraft] = useState('')

  const endRef = useRef<HTMLDivElement>(null)

  const canUpdate = !!config?.access?.canUpdateTicketStatus
  const canComment = !!config?.access?.canCommentOnClientTickets

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (!id) return
    const run = async () => {
      setLoading(true)
      try {
        const data = await api.getAdminTicket(id)
        setTicket(data.ticket)
        setMessages(data.messages)
        setStatusDraft(data.ticket.status)
        setPriorityDraft(data.ticket.priority)
      } catch (err) {
        console.error('Failed to load admin ticket:', err)
      } finally {
        setLoading(false)
      }
    }
    run()
  }, [id])

  const refreshTicket = async () => {
    if (!id) return
    const data = await api.getAdminTicket(id)
    setTicket(data.ticket)
    setMessages(data.messages)
    setStatusDraft(data.ticket.status)
    setPriorityDraft(data.ticket.priority)
  }

  const saveStatus = async () => {
    if (!id || !ticket) return
    if (!canUpdate) return

    const statusChanged = statusDraft !== ticket.status
    const priorityChanged = priorityDraft !== ticket.priority
    if (!statusChanged && !priorityChanged) return

    setSavingStatus(true)
    try {
      await api.updateAdminTicketStatus(id, {
        status: statusChanged ? statusDraft : undefined,
        priority: priorityChanged ? priorityDraft : undefined,
      })
      await refreshTicket()
    } catch (err) {
      console.error('Failed to update ticket status:', err)
    } finally {
      setSavingStatus(false)
    }
  }

  const sendComment = async () => {
    if (!id || !reply.trim()) return
    if (!canComment) return

    setSending(true)
    try {
      await api.addAdminTicketMessage(id, { content: reply.trim() })
      setReply('')
      await refreshTicket()
    } catch (err) {
      console.error('Failed to send admin comment:', err)
    } finally {
      setSending(false)
    }
  }

  if (loading) {
    return (
      <div className={styles.root}>
        <Spinner label="Loading ticket..." />
      </div>
    )
  }

  if (!ticket) {
    return (
      <div className={styles.root}>
        <Button icon={<ArrowLeft24Regular />} onClick={() => navigate('/admin')}>
          Back to Admin Dashboard
        </Button>
        <Card className={styles.section}>Ticket not found.</Card>
      </div>
    )
  }

  const status = {
    label: ticket.statusLabel || ticketStatusLabel(ticket.status, config?.statusLabels),
    color: ticketStatusColor(ticket.status),
  }

  return (
    <div className={styles.root}>
      <Button icon={<ArrowLeft24Regular />} appearance="subtle" onClick={() => navigate('/admin')}>
        Back to Admin Dashboard
      </Button>

      <Card className={styles.section}>
        <div className={styles.header}>
          <div style={{ minWidth: 0 }}>
            <div className={styles.subtitle}>{ticket.ticketNumber}</div>
            <h1 className={styles.title}>{ticket.subject}</h1>
            <div className={styles.subtitle}>
              Submitted by {ticket.senderName || ticket.senderEmail || 'Unknown'} on {formatDate(ticket.createdAt)}
            </div>
          </div>
          <Badge color={status.color} appearance="filled">
            {status.label}
          </Badge>
        </div>

        <div className={styles.row} style={{ marginTop: 12 }}>
          <Select value={statusDraft} disabled={!canUpdate || savingStatus} onChange={(_, d) => setStatusDraft(d.value)}>
            {TICKET_STATUS_KEYS.map((key) => (
              <Option key={key} value={key}>{ticketStatusLabel(key, config?.statusLabels)}</Option>
            ))}
          </Select>

          <Select value={priorityDraft} disabled={!canUpdate || savingStatus} onChange={(_, d) => setPriorityDraft(d.value)}>
            <Option value="low">Low</Option>
            <Option value="medium">Medium</Option>
            <Option value="high">High</Option>
            <Option value="urgent">Urgent</Option>
          </Select>

          <Button appearance="primary" onClick={saveStatus} disabled={!canUpdate || savingStatus}>
            {savingStatus ? 'Saving...' : 'Update Ticket'}
          </Button>
        </div>
      </Card>

      <Card className={styles.section}>
        <h3 style={{ marginTop: 0 }}>Conversation</h3>
        <div className={styles.messages}>
          {messages.map((m) => (
            <div key={m.id} className={styles.messageItem}>
              <div className={styles.meta}>
                {m.senderType} {m.isInternal ? '(internal)' : ''} · {formatDate(m.createdAt)}
              </div>
              <div className={styles.content}>{m.content}</div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
      </Card>

      <Card className={styles.section}>
        <div className={styles.composer}>
          <Textarea
            placeholder={canComment ? 'Add a comment or update for your team...' : 'You do not have comment permission'}
            value={reply}
            disabled={!canComment || sending}
            onChange={(_, d) => setReply(d.value)}
            rows={4}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              appearance="primary"
              icon={<Send24Regular />}
              onClick={sendComment}
              disabled={!canComment || sending || !reply.trim()}
            >
              {sending ? 'Sending...' : 'Send Comment'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
